import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { coreSettings } from '../../utils/core';
import { EPOCH_ISO } from '../../utils/date';
import { paymentMethodsSelectors } from '../payment-methods';
import { preferencesSelectors } from '../preferences';

import { datasetsSelectors } from '../datasets';
import { dateSelectors } from '../date';
import { SUBSCRIPTIONS_REDUCER_KEY } from './subscriptionsReducer';

export const getStore = (state) => state[SUBSCRIPTIONS_REDUCER_KEY];

export const getIsLoading = (state) => getStore(state).isLoading;
export const getLoadPending = (state) => getStore(state).loadPending;
export const getError = (state) => getStore(state).error;
export const getSubscriptions = (state) => getStore(state).resourcesById;
export const getResourcesById = (state) => getStore(state).resourcesById;

export const getActiveSubscription = createSelector(getSubscriptions, (subscriptionsById) => {
  if (subscriptionsById.size > 0) {
    return subscriptionsById
      .filter((subscription) => subscription.active && coreSettings.tiers?.includes(subscription.tierUriName))
      .sort((subscription1, subscription2) => {
        const tierComparison =
          coreSettings.tiers.indexOf(subscription1.tierUriName) - coreSettings.tiers.indexOf(subscription2.tierUriName);
        return tierComparison || subscription1.expireOn.localeCompare(subscription2.expireOn);
      })
      .last();
  }
  return null;
});

export const selectActiveOrLastSimplifiSubscription = createSelector(
  getActiveSubscription,
  getSubscriptions,
  (activeSubscription, subscriptions) =>
    activeSubscription || subscriptions?.find((subscription) => !!subscription?.tierUriName?.startsWith('acme')),
);

export const getSubscriptionTrackingProps = createSelector(
  selectActiveOrLastSimplifiSubscription,
  datasetsSelectors.getDatasetsById,
  (_state, datasetId) => datasetId,
  (subscription, datasets, datasetId) => {
    const dataset = datasetId ? datasets?.get(datasetId) : undefined;
    const props = {};
    // Following code is copied from a helper that existed before (subscriptions/helpers.js).
    // https://quickenteam.atlassian.net/browse/SIMPL-19497
    if (subscription) {
      // Retaining this existing old prop for now, it is sent to mixpanel, LD and Pendo.
      // Not sure what the impact is if I remove it.
      props.subscriptionStatus = 'subscribed';

      props.subscriber = subscription.active ? 'Subscribed' : 'Expired';

      let purchaseChannel;
      switch (subscription.merchant) {
        case 'Quicken':
          purchaseChannel = 'direct';
          break;
        case 'Apple':
          purchaseChannel = 'apple';
          break;
        case 'Google':
          purchaseChannel = 'google';
          break;
        default:
      }
      props.purchaseChannel = purchaseChannel;

      props.subscriptionTier = subscription.tierUriName;
      props.subscriptionTierName = subscription.tierName;

      if (subscription.isInTrialPeriod != null) {
        props.subscriptionState = subscription.isInTrialPeriod ? 'trial' : 'paid';
      }

      if (subscription.renewalFrequency != null) {
        props.subscriptionTerm = subscription.renewalFrequency === 'P1M' ? 'monthly' : 'annual';
      }

      props.autoRenew = subscription.autoRenew ? 'on' : 'off';
    } else if (dataset?.shared) {
      // For a shared user, we will not have access to the actual subscription record.
      // More details here: https://quickenteam.atlassian.net/browse/SIMPL-19497
      props.subscriber = 'Subscribed Shared';
    } else {
      props.subscriber = 'Not Subscribed';
      // Retaining this existing old prop for now, it is sent to mixpanel, LD and Pendo.
      // Not sure what the impact is if I remove it.
      props.subscriptionStatus = 'Not Subscribed';
    }

    return props;
  },
);

export const getProductSubscriptions = createSelector(getSubscriptions, (subscriptionsById) =>
  subscriptionsById.filter((subscription) => coreSettings.tiers?.includes(subscription.tierUriName)),
);
// ^ only gives subscriptions for tiers specified in core settings. Needed to verify product-specific subscriptions

export const subscriptionMessages = Object.freeze({
  NO_MESSAGE: 'NO_MESSAGE',
  MESSAGE1_1_ESTORE_TRIAL_CANCEL: 'MESSAGE1_1_ESTORE_TRIAL_CANCEL',
  MESSAGE1_2_MOBILE_TRIAL_CANCEL: 'MESSAGE1_2_MOBILE_TRIAL_CANCEL',
  MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL: 'MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL',
  MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL: 'MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL',
  MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL: 'MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL',
  MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL: 'MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL',
  MESSAGE4_ESTORE_NO_PAYMENT_TOKEN: 'MESSAGE4_ESTORE_NO_PAYMENT_TOKEN',
  MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD: 'MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD',
  MESSAGE6_ESTORE_GRACE_PERIOD: 'MESSAGE6_ESTORE_GRACE_PERIOD',
  MESSAGE7_MOBILE_GRACE_PERIOD: 'MESSAGE7_MOBILE_GRACE_PERIOD',
  MESSAGE9_CONVERT_MONTHLY_TO_YEARLY: 'MESSAGE9_CONVERT_MONTHLY_TO_YEARLY',
});

export const getSubscriptionMessage = createSelector(
  dateSelectors.getCurrentDateString,
  getActiveSubscription,
  paymentMethodsSelectors.getLoadPending,
  paymentMethodsSelectors.getValidPreferredPaymentMethods,
  paymentMethodsSelectors.getInvalidPreferredPaymentMethods,
  (state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user' })?.annualConversionMessageDismissedOn,
  (
    todayISO,
    subscription,
    paymentMethodsLoadPending,
    validPaymentMethods,
    invalidPaymentMethods,
    annualConversionMessageDismissedOnISO,
  ) => {
    let subscriptionMessage = subscriptionMessages.NO_MESSAGE;
    const today = DateTime.fromISO(todayISO);
    if (subscription?.active) {
      const expireOn = DateTime.fromISO(subscription.expireOn);
      const daysToExpire = expireOn.diff(today).as('days') + 1;
      if (subscription.autoRenew) {
        // subscription active
        if (subscription.isInGracePeriod) {
          // 6/7
          if (subscription.merchant === 'Quicken') {
            subscriptionMessage = subscriptionMessages.MESSAGE6_ESTORE_GRACE_PERIOD;
          } else {
            subscriptionMessage = subscriptionMessages.MESSAGE7_MOBILE_GRACE_PERIOD;
          }
        } else if (
          !paymentMethodsLoadPending &&
          !validPaymentMethods.size &&
          subscription.merchant === 'Quicken' &&
          daysToExpire <= 14
        ) {
          if (invalidPaymentMethods.size) {
            // 5
            subscriptionMessage = subscriptionMessages.MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD;
          } else {
            // 4
            subscriptionMessage = subscriptionMessages.MESSAGE4_ESTORE_NO_PAYMENT_TOKEN;
          }
        } else if (
          subscription.renewalFrequency === 'P1M' &&
          subscription.merchant === 'Quicken' &&
          daysToExpire <= 30
        ) {
          const effectiveAt = DateTime.fromISO(subscription.effectiveAt, { zone: 'utc' });
          const monthsUsed = today.diff(effectiveAt).as('months');
          if (monthsUsed >= 3) {
            const annualConversionMessageDismissedOn = DateTime.fromISO(
              annualConversionMessageDismissedOnISO || EPOCH_ISO,
              { zone: 'utc' },
            );
            const monthsPassed = today.diff(annualConversionMessageDismissedOn).as('months');
            if (monthsPassed >= 3) {
              subscriptionMessage = subscriptionMessages.MESSAGE9_CONVERT_MONTHLY_TO_YEARLY;
            }
          }
        }
      } else {
        // subscription cancelled
        // eslint-disable-next-line no-lonely-if
        if (subscription.isInTrialPeriod) {
          if (daysToExpire > 0 && daysToExpire <= 7) {
            if (subscription.merchant === 'Quicken') {
              // 1.1 - trial estore user cancelled subscription
              subscriptionMessage = subscriptionMessages.MESSAGE1_1_ESTORE_TRIAL_CANCEL;
            } else {
              // 1.2 - trial appstore user cancelled subscription
              subscriptionMessage = subscriptionMessages.MESSAGE1_2_MOBILE_TRIAL_CANCEL;
            }
          }
        } else if (subscription.renewalFrequency === 'P1M' && daysToExpire > 0 && daysToExpire <= 7) {
          if (subscription.merchant === 'Quicken') {
            // 2.1 - paid estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL;
          } else {
            // 2.2 - paid non estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL;
          }
        } else if (subscription.renewalFrequency === 'P1Y' && daysToExpire > 0 && daysToExpire <= 30) {
          if (subscription.merchant === 'Quicken') {
            // 3.1 - paid estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL;
          } else {
            // 3.2 - paid non estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL;
          }
        }
      }
    }

    return subscriptionMessage;
  },
);
